/**
 * Cache Management Utility
 * Handles cache-busting strategies to ensure users always get the latest UI updates
 */

export interface CacheConfig {
    version: string;
    timestamp: number;
    components: string[];
}

const CACHE_KEY = 'memer_ui_cache_version';
const CURRENT_VERSION = '2.5.0'; // Phase 2.5 with smart number formatting

/**
 * Get current cache configuration
 */
export function getCurrentCacheConfig(): CacheConfig {
    return {
        version: CURRENT_VERSION,
        timestamp: Date.now(),
        components: [
            'SmartHexDisplay',
            'SmartNumberDisplay',
            'InlineConverter',
            'FloatingConverter',
            'FunctionExplorer',
            'TransactionBuilder'
        ]
    };
}

/**
 * Check if cache needs to be cleared
 */
export function shouldClearCache(): boolean {
    try {
        const stored = localStorage.getItem(CACHE_KEY);
        if (!stored) return true;

        const storedConfig: CacheConfig = JSON.parse(stored);
        const current = getCurrentCacheConfig();

        // Clear cache if version changed
        if (storedConfig.version !== current.version) {
            console.log(`[CacheManager] Version changed: ${storedConfig.version} → ${current.version}`);
            return true;
        }

        // Clear cache if older than 1 hour (for development)
        const oneHour = 60 * 60 * 1000;
        if (Date.now() - storedConfig.timestamp > oneHour) {
            console.log(`[CacheManager] Cache expired (${Math.round((Date.now() - storedConfig.timestamp) / 1000 / 60)} minutes old)`);
            return true;
        }

        return false;
    } catch (error) {
        console.warn('[CacheManager] Error checking cache:', error);
        return true;
    }
}

/**
 * Clear all application caches
 */
export async function clearAllCaches(): Promise<void> {
    try {
        // Clear localStorage cache markers
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('memer_ui_') || key.includes('cache'))) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));

        // Clear browser caches if available
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
            );
            console.log('[CacheManager] Cleared service worker caches');
        }

        // Force reload stylesheets
        const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
        stylesheets.forEach((link: any) => {
            const href = link.href;
            link.href = href.includes('?')
                ? `${href}&v=${Date.now()}`
                : `${href}?v=${Date.now()}`;
        });

        console.log('[CacheManager] All caches cleared');
    } catch (error) {
        console.error('[CacheManager] Error clearing caches:', error);
    }
}

/**
 * Update cache version
 */
export function updateCacheVersion(): void {
    try {
        const config = getCurrentCacheConfig();
        localStorage.setItem(CACHE_KEY, JSON.stringify(config));
        console.log(`[CacheManager] Cache version updated to ${config.version}`);
    } catch (error) {
        console.error('[CacheManager] Error updating cache version:', error);
    }
}

/**
 * Initialize cache management
 */
export async function initializeCacheManagement(): Promise<void> {
    console.log('[CacheManager] Initializing cache management...');

    if (shouldClearCache()) {
        console.log('[CacheManager] Cache needs clearing...');
        await clearAllCaches();
        updateCacheVersion();
    } else {
        console.log('[CacheManager] Cache is up to date');
    }
}

/**
 * Add cache-busting parameters to URLs
 */
export function addCacheBuster(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${CURRENT_VERSION}&t=${Date.now()}`;
}

/**
 * Force refresh component by updating its key
 */
export function getComponentKey(componentName: string): string {
    return `${componentName}_${CURRENT_VERSION}_${Date.now()}`;
}

/**
 * Check if running in development mode
 */
export function isDevelopmentMode(): boolean {
    return process.env.NODE_ENV === 'development' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
}

/**
 * Enhanced cache clearing for development
 */
export async function developmentCacheClear(): Promise<void> {
    if (!isDevelopmentMode()) return;

    console.log('[CacheManager] Development cache clear...');

    // Clear all localStorage
    localStorage.clear();

    // Clear sessionStorage
    sessionStorage.clear();

    // Clear IndexedDB if available
    if ('indexedDB' in window) {
        try {
            const databases = await indexedDB.databases();
            await Promise.all(
                databases.map(db => {
                    if (db.name) {
                        return new Promise((resolve, reject) => {
                            const deleteReq = indexedDB.deleteDatabase(db.name!);
                            deleteReq.onsuccess = () => resolve(undefined);
                            deleteReq.onerror = () => reject(deleteReq.error);
                        });
                    }
                })
            );
        } catch (error) {
            console.warn('[CacheManager] Could not clear IndexedDB:', error);
        }
    }

    await clearAllCaches();

    console.log('[CacheManager] Development cache cleared completely');
}
